import { SegmentedControl, Stack, Text } from "@mantine/core";
import { useMemo, useState } from "react";

import { useGetSurveys } from "../../../api/insights";
import { ChartFrame, QuestionList } from "../charts";
import SectionShell, { Stat, formatNum, formatPct } from "../SectionShell";

const QUESTION_TYPE_LABEL = {
  multiple_choice: "Multiple choice",
  single_choice: "Single choice",
  text: "Text",
};

const toItem = (question) => {
  const isText = question.questionType === "text";
  const responses = isText
    ? (question.textAnswers ?? []).filter((a) => String(a ?? "").trim()).length
    : (question.options ?? []).reduce(
        (sum, o) => sum + (Number(o.count) || 0),
        0
      );
  return {
    id: question.questionId,
    title: question.questionText ?? "(untitled question)",
    meta: `${QUESTION_TYPE_LABEL[question.questionType] ?? question.questionType ?? "—"} · ${formatNum(responses)} responses`,
    kind: isText ? "text" : "choice",
    options: question.options,
    answers: question.textAnswers,
  };
};

function SurveysAnalysis({ eventId }) {
  const { data, isPending, isError, error } = useGetSurveys(eventId);
  const surveys = useMemo(() => data?.surveys ?? [], [data]);
  const [selectedId, setSelectedId] = useState(null);
  const activeId = surveys.some((s) => String(s.surveyId) === selectedId)
    ? selectedId
    : String(surveys[0]?.surveyId ?? "");

  return (
    <SectionShell
      id="surveysAnalysis"
      title="Surveys"
      description="Post-event survey participation and completion."
      isPending={isPending}
      isError={isError}
      error={error}
      eventId={eventId}
      analysisKey="surveysAnalysis"
      stats={
        <>
          <Stat
            part="totalSurveys"
            label="Total surveys"
            value={formatNum(data?.totalSurveys)}
          />
          <Stat
            part="totalResponses"
            label="Total responses"
            value={formatNum(data?.totalResponses)}
          />
          <Stat
            part="participants"
            label="Participants"
            value={formatNum(data?.participants)}
          />
          <Stat
            part="participationRate"
            label="Participation rate"
            value={formatPct(data?.participationRate)}
          />
        </>
      }
      lead={
        <ChartFrame
          part="perSurveyBreakdown"
          title="Per-survey results"
          hasData={surveys.length > 0}
        >
          {surveys.length > 1 && (
            <SegmentedControl
              className="insights-print-hide"
              size="xs"
              value={activeId}
              onChange={setSelectedId}
              data={surveys.map((s) => ({
                value: String(s.surveyId),
                label: s.title ?? "(untitled survey)",
              }))}
              style={{ alignSelf: "flex-start" }}
            />
          )}
          {surveys.map((s) => (
            <Stack
              key={s.surveyId}
              gap="xs"
              className="insights-survey"
              data-active={String(String(s.surveyId) === activeId)}
            >
              <Text fw={600} className="insights-print-only">
                {s.title ?? "(untitled survey)"}
              </Text>
              <Text size="xs" c="dimmed">
                {formatNum(s.starters)} starters · {formatNum(s.completers)}{" "}
                completers · {formatPct(s.completionRate)} completion ·{" "}
                {formatNum(s.totalResponses)} responses
              </Text>
              <QuestionList items={(s.questions ?? []).map(toItem)} />
            </Stack>
          ))}
        </ChartFrame>
      }
    />
  );
}

export default SurveysAnalysis;
